import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BrandMark } from "@/components/layout/Brand";

export default function LoginPage({ session }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await session.login({ email, password });
      nav("/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Nie udało się zalogować.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#061327]">
      <Helmet>
        <title>Logowanie — Kurdynowski CRM</title>
      </Helmet>

      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(1200px 600px at 20% 20%, rgba(224,122,19,0.18), transparent 60%), radial-gradient(1000px 600px at 80% 0%, rgba(61,106,255,0.18), transparent 50%)",
          }}
        />

        <div className="relative mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-14 items-start">
            <div className="text-white">
              <div className="flex items-center gap-3">
                <BrandMark className="h-10 w-auto" />
                <div>
                  <div className="text-sm font-semibold tracking-tight" data-testid="login-brand-title">
                    Kurdynowski CRM
                  </div>
                  <div className="text-xs text-white/70" data-testid="login-brand-subtitle">
                    Accounting & Tax Solutions
                  </div>
                </div>
              </div>

              <h1
                data-testid="login-hero-title"
                className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-heading"
              >
                Bezpieczna obsługa klientów,
                <span className="text-[#E07A13]"> premium</span> workflow.
              </h1>
              <p data-testid="login-hero-subtitle" className="mt-5 text-base md:text-lg text-white/75 max-w-xl">
                CRM dla Biura Rachunkowego Kurdynowski: klienci, zadania, kalendarz, oś czasu, raporty czasu oraz faktury.
              </p>

              <div className="mt-6 text-xs text-white/60" data-testid="login-gdpr-note">
                Dostęp i podgląd treści są audytowane. Minimalizuj dane wrażliwe zgodnie z RODO.
              </div>
            </div>

            <div>
              <Card className="rounded-2xl border-slate-200/70 shadow-xl shadow-black/10">
                <CardHeader>
                  <CardTitle data-testid="login-form-title" className="text-xl">Zaloguj się</CardTitle>
                </CardHeader>
                <CardContent>
                  {error ? (
                    <Alert data-testid="login-error-alert" className="mb-4" variant="destructive">
                      <AlertTitle>Błąd</AlertTitle>
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  ) : null}

                  <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
                    <div>
                      <label className="text-sm font-medium text-slate-700" htmlFor="email">
                        E-mail
                      </label>
                      <Input
                        data-testid="login-email-input"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="np. imie.nazwisko@kurdynowski.pl"
                        className="mt-1 h-11 rounded-xl"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700" htmlFor="password">
                        Hasło
                      </label>
                      <Input
                        data-testid="login-password-input"
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 h-11 rounded-xl"
                      />
                    </div>

                    <Button
                      data-testid="login-submit-button"
                      type="submit"
                      className="w-full h-11 rounded-full bg-[#0A1C3A] hover:bg-[#07152d]"
                      disabled={loading}
                    >
                      {loading ? "Logowanie…" : "Zaloguj"}
                    </Button>

                    <div className="flex items-center justify-between pt-1">
                      <Link
                        to="/zapomniane-haslo"
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Zapomniałem hasła
                      </Link>
                      <span className="text-xs text-slate-500" data-testid="login-security-hint">
                        Auto-wylogowanie po 30 min
                      </span>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Legal Links */}
              <div className="mt-4 text-center">
                <Link
                  to="/dokumentacja-prawna"
                  className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                  data-testid="legal-link"
                >
                  Regulamin · Polityka prywatności · Prawa autorskie
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
